import { useState } from "react";
import { useElements, useStripe, PaymentElement } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  amountLabel: string;
  onPaid: () => Promise<void>;
}

const ReservationCheckoutForm = ({ amountLabel, onPaid }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentComplete, setPaymentComplete] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setError("");

    if (paymentComplete) {
      await onPaid();
      setIsLoading(false);
      return;
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Your payment could not be completed.");
      setIsLoading(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      setPaymentComplete(true);
      await onPaid();
      setIsLoading(false);
      return;
    }

    if (paymentIntent?.status === "processing") {
      setPaymentComplete(true);
      await onPaid();
      setIsLoading(false);
      return;
    }

    setError("Your payment was not completed. Please try again.");
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || !elements || isLoading}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {isLoading ? "Processing" : paymentComplete ? "Retry confirmation" : `Pay ${amountLabel} and reserve`}
      </Button>

      <p className="text-center text-xs text-foreground/50">
        Payments are handled securely by Stripe. Your card details never touch our servers.
      </p>
    </form>
  );
};

export default ReservationCheckoutForm;
